import { AutoComplete, Listbox } from "../../styles/global.style";
import { Box, FormControl, InputAdornment, InputLabel, Typography, styled } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import React, { ChangeEvent, useEffect, useState } from "react";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

import ExpandDownIcon from "../Icons/ExpandDownIcon";
import ExpandUpIcon from "../Icons/ExpandUpIcon";
import { ILocationSearchPrediction } from "../../services/location/location.interface";
import { InputCustom } from "../../styles/styledComponents";
import Loading from "../Loading/Loading";
import PetAddressIcon from "../../components/Icons/PetAddressIcon";
import { paletteColors } from "../../styles/muiTheme";
import { useDebounce } from "../../hooks";
import { useLocationSearch } from "../../services/location/location.service";

interface AutocompleteLocationSearchProps {
  id?: string;
  onSelect: (breed: ILocationSearchPrediction) => void;
  searchValue: string;
  expand?: boolean | null;
  handleToggleLoctionsFields?: any
  label?: string;
  control?: Control<any>;
  disable?: boolean,
  setShowAddressFields? : any | null;
  setIsBubble?: (value: boolean) => void;
  isExpandIcon?:boolean
  className?:string;
}

const GvAutocompleteLocationSearch = ({
  id,
  onSelect,
  searchValue,
  expand = null,
  handleToggleLoctionsFields,
  label,
  control,
  disable,
  setShowAddressFields,
  isExpandIcon=true,
  setIsBubble,
  className,
}: AutocompleteLocationSearchProps) => {
  const [showPrediction, setShowPrediction] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const { findLocation, locations, loading } = useLocationSearch();
  const debouncedValue = useDebounce<string>(search, 500);
  const geoCodingApi = process.env.REACT_APP_GEOAPIFY_API_KEY;

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}/>
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: paletteColors.sand.main,
      color: paletteColors.black.main,
      padding: ".5rem 1rem",
      border: `1px solid ${paletteColors.taupe.dark}`,
    },
  });

  const handleSelectPlace = (place: ILocationSearchPrediction) => {
    setSearch(place.description);
    setShowPrediction(false);
    onSelect(place);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setShowPrediction(true);
  };

  useEffect(() => {
    if (debouncedValue) {
      findLocation(debouncedValue);
    }
  }, [debouncedValue]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function(pos) {
        const reverseGeocodingUrl =
        `https://api.geoapify.com/v1/geocode/reverse?lat=
        ${pos.coords.latitude}&lon=${pos.coords.longitude}
        &apiKey=${geoCodingApi}`;

        setGeoLoading(true);

        fetch(reverseGeocodingUrl)
          .then((result) => result.json())
          .then((res) => {
            const properties = res.features[0].properties;

            setGeoLoading(false);
            setSearch(properties.formatted);
            setShowPrediction(true);
            const place = {
              description: properties?.formatted,
              place_id: "",
            };

            onSelect(place);
          });
      });
    }
  };

  useEffect(() => {
    setSearch(searchValue);
  }, [searchValue]);

  return (
    <AutoComplete>
      <FormControl className="w-full">
        <InputLabel
          htmlFor={id}
          className={`w-full ${className}`}
        >
          {label}
          {isExpandIcon && <div className="float-right mr-3.5" onClick={handleToggleLoctionsFields}>
            {
              expand !== null && (expand ?
                <ExpandUpIcon width={20} height={20} /> :
                <ExpandDownIcon width={20} height={20} />)
            }
          </div> }
        </InputLabel>
        <Controller
          name="address"
          control={control}
          render={({ field, fieldState }) =>
            <InputCustom
              id={id}
              type="text"
              placeholder="Address"
              value={search}
              onChange={handleChange}
              onFocus={() => {
                setShowPrediction(true);
                setIsBubble && setIsBubble(false);
              }}
              autoComplete="off"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={!disable}
              onFocusCapture={() => setShowAddressFields && setShowAddressFields(true)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="mr-5">
                    {(loading || geoLoading) ? <Loading size={26} /> : null}
                  </InputAdornment>
                ),
              }}
            />
          }
        />
        {disable && <Box
          onClick={getLocation}
          className="absolute right-2 top-7 p-1 cursor-pointer"
        >
          <HtmlTooltip
            title={
              <Typography color="inherit">Click/tap here to find your location</Typography>
            }
            placement="top"
            open={open}
          >
            <Box
              className="grid ml-1.5"
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              onTouchStart={() => setOpen(true)}
            >
              <PetAddressIcon />
            </Box>
          </HtmlTooltip>
        </Box>}

        {showPrediction && locations && locations.predictions?.length > 0 ? (
          <Listbox>
            {(locations?.predictions).map((place) => (
              <li key={place.place_id} onClick={() => handleSelectPlace(place)}>
                {place.description}
              </li>
            ))}
          </Listbox>
        ) : null}
      </FormControl>
    </AutoComplete>

  );
};

export default GvAutocompleteLocationSearch;
